import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../types'
import {
    BASE_HORIZONTAL_PADDING,
    CENTER_COLUMN_PINNED_RANGES,
    CENTER_ROW_PINNED_RANGES,
    CORNER_PINNED_RANGES,
    CTX_ABSOLUTE_AVAILABLE_WIDTH,
    HEADER_COMPONENTS_WIDTH,
    LOGO_RATIOS
} from './constants'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getTargetLayoutPointer
} from '../../conversionDal/utils/pointer'
import {
    getParent,
    hasMenuBrother,
    isChildOfHeader,
    isHeaderFirstRowChildComponent,
    isInternalBox,
    isMenuContainerChild,
    isPinnedToContainer,
    isPlugin,
    isSection,
    isSectionsRootContainer,
    isSuperGrid,
    isSuperGridCell,
    predicatePinnedHeuristic,
    updateTargetLayout
} from '../common'
import type {Pointer} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import {Orientation} from '../../enums'
import {VIEW_MODES} from '../../../../constants/constants'
import type {StructureEntryItem} from '../../conversionDal/types'
import {pointerUtils} from '@wix/document-manager-core'

export const getAvailableWidthKey = (componentPointer: Pointer) =>
    `${CTX_ABSOLUTE_AVAILABLE_WIDTH}-${componentPointer.id}`

const getAvailableWidth = (contextHelper: PluginContext, componentPointer: Pointer) => {
    return contextHelper.get(getAvailableWidthKey(componentPointer))
}

const getAvailableWidthForParent = (componentStageData: ComponentStageData, contextHelper: PluginContext): number => {
    const {componentPointer, conversionDal, config} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    return parentPointer ? getAvailableWidth(contextHelper, parentPointer) : config.targetCanvasSize
}

const updateComponentWidth = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    width: number,
    availableWidth: number = getAvailableWidthForParent(componentStageData, contextHelper)
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}
    contextHelper.set(getAvailableWidthKey(componentPointer), width)

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            width: {
                value: (width / availableWidth) * 100,
                type: 'percentage'
            },
            type: 'ComponentLayout'
        }
    })
}

const setMaxContentWidth = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}

    updateTargetLayout(componentStageData, {
        componentLayout: {
            ...componentLayout,
            width: {
                type: 'maxContent'
            },
            type: 'ComponentLayout'
        }
    })
}

const updatePinnedHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer, config} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right

    const relativeSize = config.targetCanvasSize
    if (!target) target = (marginToUpdate.value * config.sourceCanvasSize) / 100
    marginToUpdate.value = (target / relativeSize) * 100

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'FixedItemLayout'
        })
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(
        getAvailableWidthKey(componentPointer),
        availableWidth - (marginToUpdate.value * relativeSize) / 100 - BASE_HORIZONTAL_PADDING
    )
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerHorizontalMargins = (
    componentStageData: ComponentStageData,
    target: number | undefined,
    contextHelper: PluginContext
) => {
    const {conversionDal, componentPointer, initialSnapshot} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getSourceLayoutPointer(componentPointer)))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {left, right} = margins

    const marginToUpdate = left?.value > 0 ? left : right

    const relativeSize = contextHelper.get(getAvailableWidthKey(getParent(conversionDal, componentPointer)!))
    const parentDesktopWidth = initialSnapshot.getValue(
        getMeasurementsPointer(getParent(conversionDal, componentPointer)!)
    ).width

    if (!target) target = (marginToUpdate.value * parentDesktopWidth) / 100
    marginToUpdate.value = (target / relativeSize) * 100

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                left,
                right
            },
            type: 'GridItemLayout'
        })
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    contextHelper.set(
        getAvailableWidthKey(componentPointer),
        availableWidth - (marginToUpdate.value * relativeSize) / 100 - BASE_HORIZONTAL_PADDING
    )
}

const updateHorizontalMargins = (
    componentStageData: ComponentStageData,
    contextHelper: PluginContext,
    left: number,
    availableWidth: number = getAvailableWidthForParent(componentStageData, contextHelper),
    stretchComponent: boolean = false
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    contextHelper.set(getAvailableWidthKey(componentPointer), availableWidth - 2 * left)

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, itemLayout, {
            ...(stretchComponent && {
                justifySelf: 'stretch'
            }),
            margins: {
                left: {
                    value: (left / availableWidth) * 100,
                    type: 'percentage'
                },
                right: {
                    value: (left / availableWidth) * 100,
                    type: 'percentage'
                }
            }
        })
    })
}

const updateAvailableWidthForBoxPlugin = (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
    const {config, componentPointer} = componentStageData
    contextHelper.set(getAvailableWidthKey(componentPointer), config.targetCanvasSize)
}

const updateSuperGridHorizontalPadding = (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
    const {conversionDal, componentPointer} = componentStageData
    const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}

    const sourcePadding = sourceLayout?.containerLayout?.padding

    _.set(containerLayout, ['padding', 'left'], {
        value: sourcePadding?.left?.value ? 24 : 0,
        type: 'spx'
    })

    _.set(containerLayout, ['padding', 'right'], {
        value: sourcePadding?.right?.value ? 24 : 0,
        type: 'spx'
    })

    _.set(containerLayout, ['columnGap'], {
        value: sourceLayout?.containerLayout?.columnGap?.value ? 24 : 0,
        type: 'spx'
    })

    updateTargetLayout(componentStageData, {
        containerLayout
    })

    const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
    const totalPadding = (containerLayout.padding?.left?.value || 0) + (containerLayout.padding?.right?.value || 0)

    contextHelper.set(getAvailableWidthKey(componentPointer), availableWidth - totalPadding)
}

const findIndexInHeaderFirstRow = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const component = conversionDal.get(componentPointer)
    const containerPointer = getStructurePointer({id: component.parent})
    const children = conversionDal.get(containerPointer)?.components ?? []

    return children?.findIndex((childId: string) => childId === componentPointer.id)
}

const calcTotalWidthToMeInHeader = (componentStageData: ComponentStageData, myIndex: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const containerPointer = getParent(conversionDal, componentPointer)
    const children = conversionDal.get(containerPointer!)?.components ?? []
    return children.slice(0, myIndex + 1).reduce((acc: number, childId: string) => {
        const measurements = conversionDal.get(getMeasurementsPointer({id: childId}))
        return acc + (measurements?.width ?? 0)
    }, 0)
}

const getFallbackRatio = ({componentPointer, initialSnapshot}: ComponentStageData) => {
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    return originalMeasurements.height / originalMeasurements.width
}

const isInRange = (value: number, min: number, max?: number) => {
    return value >= min && value < (max ?? Infinity)
}

export const horizontalSpacingHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: ({componentPointer, config}: ComponentStageData, contextHelper: PluginContext) =>
            contextHelper.set(getAvailableWidthKey(componentPointer), config.targetCanvasSize),
        heuristicCode: 'SECTION X'
    },
    ...CORNER_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {config, initialSnapshot, conversionDal, componentPointer} = componentStageData
            const relativeSize = isPinnedToContainer(componentStageData)
                ? initialSnapshot.getValue(getMeasurementsPointer(getParent(conversionDal, componentPointer)!)).width
                : config.sourceCanvasSize
            return predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                (min / relativeSize) * 100,
                max ? (max / relativeSize) * 100 : max,
                (justifySelf, alignSelf) => alignSelf !== 'center' && justifySelf !== 'center'
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CORNER PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_COLUMN_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {config, initialSnapshot, conversionDal, componentPointer} = componentStageData
            const relativeSize = isPinnedToContainer(componentStageData)
                ? initialSnapshot.getValue(getMeasurementsPointer(getParent(conversionDal, componentPointer)!)).width
                : config.sourceCanvasSize
            return predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                (min / relativeSize) * 100,
                max ? (max / relativeSize) * 100 : max,
                justifySelf => justifySelf === 'center'
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER COLUMNED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    ...CENTER_ROW_PINNED_RANGES.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {config, initialSnapshot, conversionDal, componentPointer} = componentStageData
            const relativeSize = isPinnedToContainer(componentStageData)
                ? initialSnapshot.getValue(getMeasurementsPointer(getParent(conversionDal, componentPointer)!)).width
                : config.sourceCanvasSize
            return predicatePinnedHeuristic(
                componentStageData,
                Orientation.Horizontal,
                (min / relativeSize) * 100,
                max ? (max / relativeSize) * 100 : max,
                (justifySelf, alignSelf) => alignSelf === 'center'
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerHorizontalMargins(componentStageData, target, contextHelper)
                return
            }
            updatePinnedHorizontalMargins(componentStageData, target, contextHelper)
        },
        heuristicCode: `CENTER ROWED PINNED ELEMENT ${min}-${max} -> ${target}`
    })),
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return (
                isHeaderFirstRowChildComponent(componentPointer, conversionDal) &&
                component.semanticType === SemanticTypes.MENU
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
            updateHorizontalMargins(
                componentStageData,
                contextHelper,
                availableWidth - HEADER_COMPONENTS_WIDTH.MENU_WIDTH - HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS
            )
        },
        heuristicCode: 'MENU IN HEADER FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return (
                isHeaderFirstRowChildComponent(componentPointer, conversionDal) &&
                component.semanticType === SemanticTypes.LOGO
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS),
        heuristicCode: 'LOGO IN HEADER FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            if (!isHeaderFirstRowChildComponent(componentPointer, conversionDal)) {
                return false
            }

            const parentPointer = getParent(conversionDal, componentPointer)

            return hasMenuBrother(parentPointer!, conversionDal)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
            const myIndex = findIndexInHeaderFirstRow(componentStageData)
            updateHorizontalMargins(
                componentStageData,
                contextHelper,
                availableWidth -
                    HEADER_COMPONENTS_WIDTH.HEADER_COMPONENT_GAP * myIndex -
                    HEADER_COMPONENTS_WIDTH.COMPONENT_IN_HEADER_WIDTH * myIndex -
                    HEADER_COMPONENTS_WIDTH.MENU_WIDTH -
                    HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS
            )
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW WITH MENU'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isHeaderFirstRowChildComponent(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const availableWidth = getAvailableWidthForParent(componentStageData, contextHelper)
            const myIndex = findIndexInHeaderFirstRow(componentStageData)
            const calculateTotalWidth = calcTotalWidthToMeInHeader(componentStageData, myIndex)
            updateHorizontalMargins(
                componentStageData,
                contextHelper,
                availableWidth -
                    HEADER_COMPONENTS_WIDTH.HEADER_COMPONENT_GAP * myIndex -
                    calculateTotalWidth -
                    HEADER_COMPONENTS_WIDTH.HEADER_SIDE_MARGINS
            )
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW'
    },
    {
        predicate: isSuperGrid,
        apply: updateSuperGridHorizontalPadding,
        heuristicCode: 'SUPERGRID HORIZONTAL PADDING AND COLUMN GAP'
    },
    {
        predicate: isInternalBox,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            contextHelper.set(
                getAvailableWidthKey(componentPointer),
                getAvailableWidthForParent(componentStageData, contextHelper)
            )
        }
    },
    {
        predicate: isPlugin,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateAvailableWidthForBoxPlugin(componentStageData, contextHelper)
        },
        heuristicCode: 'BOX PLUGIN'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: '0 HORIZONTAL MARGINS FOR SUPERGRID CELL'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.BOX) {
                return false
            }

            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({conversionDal, componentPointer: parentPointer!})
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: 'BOX WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.IMAGE) {
                return false
            }

            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({conversionDal, componentPointer: parentPointer!})
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, 0, undefined, true),
        heuristicCode: 'IMAGE WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateHorizontalMargins(componentStageData, contextHelper, BASE_HORIZONTAL_PADDING),
        heuristicCode: 'FALLBACK X'
    }
]

export const widthHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return isChildOfHeader(componentPointer, conversionDal) && component.semanticType === SemanticTypes.MENU
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.MENU_WIDTH),
        heuristicCode: 'MENU IN HEADER'
    },
    ...LOGO_RATIOS.map(({min, max, target}) => ({
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)
            const {componentLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
            const ratio =
                componentLayout?.height?.type === 'aspectRatio'
                    ? componentLayout.height.value
                    : getFallbackRatio(componentStageData)
            return component.semanticType === SemanticTypes.LOGO && isInRange(ratio, min, max)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, target),
        heuristicCode: `LOGO WITH ASPECT RATIO WIDTH ${target}`
    })),
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isChildOfHeader(componentPointer, conversionDal) && hasMenuBrother(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
            updateComponentWidth(componentStageData, contextHelper, HEADER_COMPONENTS_WIDTH.COMPONENT_IN_HEADER_WIDTH),
        heuristicCode: 'COMPONENT IN HEADER THAT HAS MENU'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isHeaderFirstRowChildComponent(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {conversionDal, componentPointer} = componentStageData
            const width = conversionDal.get(getMeasurementsPointer(componentPointer))?.width
            updateComponentWidth(componentStageData, contextHelper, width)
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            if (!originalMeasurements?.width) {
                return
            }
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            if (!availableWidth) {
                return
            }
            const width = Math.min(originalMeasurements.width, availableWidth)
            updateComponentWidth(componentStageData, contextHelper, width, availableWidth)
        },
        heuristicCode: 'MENU CONTAINER CHILD PROPORTIONAL WIDTH'
    },
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: ({componentPointer, config}: ComponentStageData, contextHelper: PluginContext) =>
            contextHelper.set(getAvailableWidthKey(componentPointer), config.targetCanvasSize)
    },
    {
        predicate: isInternalBox,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidthForParent(componentStageData, contextHelper)
            )
        },
        heuristicCode: 'STRETCH PLUGIN IN PLACEHOLDER'
    },
    {
        predicate: isPlugin,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, config} = componentStageData
            updateComponentWidth(
                componentStageData,
                contextHelper,
                getAvailableWidth(contextHelper, componentPointer),
                config.targetCanvasSize
            )
        },
        heuristicCode: 'STRETCH PLUGIN IN PLACEHOLDER'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'BOX WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            if (conversionDal.get(componentPointer).semanticType !== SemanticTypes.IMAGE) {
                return false
            }
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            const parentPointer = getParent(conversionDal, componentPointer)

            if (!parentPointer) {
                return false
            }

            const parentOriginalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(parentPointer))

            return (
                Math.abs(originalMeasurements.width - parentOriginalMeasurements.width) <= 6 &&
                isSectionsRootContainer({conversionDal, componentPointer: parentPointer!})
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)

            updateComponentWidth(componentStageData, contextHelper, availableWidth)
        },
        heuristicCode: 'IMAGE WIDTH IS THE APPROXIMATELY THE SAME SIZE OF THE PARENT SECTION'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.IMAGE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'IMAGE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer, initialSnapshot}: ComponentStageData) => {
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG &&
                originalMeasurements.width <= 100
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.SVG,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width * 0.7, availableWidth)
            )
        },
        heuristicCode: 'SVG WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.TEXT,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'TEXT WIDTH'
    },
    {
        predicate: (
            {conversionDal, componentPointer}: ComponentStageData,
            _contextHelper?: PluginContext,
            readOnlyExtensionApi?: ReadOnlyExtensionAPI
        ) =>
            !!readOnlyExtensionApi?.manifest.layout.isContentWidth(
                pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP)
            ) &&
            (conversionDal.get(componentPointer) as StructureEntryItem).semanticType === SemanticTypes.HORIZONTAL_LINE,
        apply: (componentStageData: ComponentStageData) => setMaxContentWidth(componentStageData)
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {initialSnapshot, conversionDal, componentPointer} = componentStageData
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            return (
                conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE &&
                originalMeasurements.width <= 200
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {initialSnapshot, componentPointer} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))

            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'NARROW HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.HORIZONTAL_LINE,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'HORIZONTAL LINE WIDTH'
    },
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BUTTON,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, initialSnapshot} = componentStageData
            const availableWidth = getAvailableWidth(contextHelper, componentPointer)
            const originalMeasurements = initialSnapshot.getValue(getMeasurementsPointer(componentPointer))
            updateComponentWidth(
                componentStageData,
                contextHelper,
                Math.min(originalMeasurements.width, availableWidth)
            )
        },
        heuristicCode: 'BUTTON WIDTH'
    },
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer} = componentStageData
            updateComponentWidth(componentStageData, contextHelper, getAvailableWidth(contextHelper, componentPointer))
        },
        heuristicCode: 'FALLBACK WIDTH'
    }
]
